import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import RichTextEditor from '../components/RichTextEditor';
import { ChevronLeft, ChevronRight, Plus, Trash2, Send, Users, FileSpreadsheet, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['Template', 'Details', 'Recipients', 'Preview & Send'];

// Parse pasted CSV — handles "name,email,phone" or "name,email" or "email,phone"
const parsePasted = (text) => {
  const out = [];
  const lines = (text || '').split(/\r?\n/);
  for (const ln of lines) {
    const trimmed = ln.trim();
    if (!trimmed) continue;
    const parts = trimmed.split(/[,\t]/).map((s) => s.trim());
    let name = '', email = '', phone = '';
    parts.forEach((p) => {
      if (!p) return;
      if (p.includes('@')) email = p;
      else if (/^\+?\d[\d\s\-().]{6,}$/.test(p)) phone = p;
      else if (!name) name = p;
    });
    if (email || phone) out.push({ name: name || 'Guest', email, phone });
  }
  return out;
};

const InviteCreate = () => {
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('fruit_fiesta');
  // Iter-90: Booking link is now MANDATORY. Fetched on mount; if none, we
  // show an empty-state instead of the form.
  const [upcomingBookings, setUpcomingBookings] = useState(null);   // null while loading
  const [selectedBookingId, setSelectedBookingId] = useState('');
  // iter-135 — track Pay-Later holds separately so we can show a specific
  // "complete payment first" message instead of the generic empty state.
  const [heldBookings, setHeldBookings] = useState([]);
  const [form, setForm] = useState({
    child_name: '',
    age: '',
    party_date: '',
    party_time: '10:00 AM',
    location: 'Creative Birthdays Studio',
    message: "We'd love for you to celebrate with us!",
    photo_url: '',
    send_email: true,
    send_sms: false,
  });
  const [recipientRows, setRecipientRows] = useState([{ name: '', email: '', phone: '' }]);
  const [pasted, setPasted] = useState('');
  const [creating, setCreating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API_URL}/api/invite-templates`).then((r) => setTemplates(r.data || [])).catch(() => {});
    const fromBooking = searchParams.get('from_booking');
    if (fromBooking) {
      // Explicit pre-fill from a chosen booking
      axios.get(`${API_URL}/api/bookings/${fromBooking}/invite-data`, { withCredentials: true })
        .then((r) => {
          const d = r.data || {};
          setForm((f) => ({
            ...f,
            child_name: d.child_name || f.child_name,
            age: d.age || f.age,
            party_date: d.party_date || f.party_date,
            party_time: d.party_time || f.party_time,
            location: d.location || f.location,
            message: d.default_message || f.message,
          }));
          toast.success('Pre-filled from your booking');
        })
        .catch((e) => {
          if (e.response?.status === 401) navigate('/login?next=' + encodeURIComponent(window.location.pathname + window.location.search));
          else toast.error('Could not load booking details');
        });
      return;
    }
    // Otherwise, fall back to latest confirmed/pending booking
    axios.get(`${API_URL}/api/bookings/user/my-bookings`, { withCredentials: true })
      .then((r) => {
        const upcoming = (r.data || []).find((b) => b.status === 'confirmed' || b.status === 'pending');
        if (upcoming) {
          setForm((f) => ({
            ...f,
            child_name: upcoming.child_name || f.child_name,
            age: upcoming.child_age || f.age,
            party_date: upcoming.date || f.party_date,
            party_time: upcoming.time_slot === '10:00' ? '10:00 AM' : upcoming.time_slot === '13:00' ? '1:00 PM' : '4:00 PM',
          }));
        }
      })
      .catch(() => { /* not logged in or no bookings — fine */ });
  }, []);

  // Iter-90: Fetch the customer's upcoming bookings — invitation creation is
  // gated on having at least one. If a `booking_id` URL param is provided,
  // pre-select it; otherwise pre-select the first upcoming booking.
  // iter-135 — also fetch held bookings so we can show the specific
  // "complete payment to confirm" message when relevant.
  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/customer/upcoming-bookings`, { withCredentials: true }).then((r) => r.data || []).catch(() => []),
      axios.get(`${API_URL}/api/bookings/user/my-bookings`, { withCredentials: true }).then((r) => r.data || []).catch(() => []),
    ]).then(([list, all]) => {
      setUpcomingBookings(list);
      const todayIso = new Date().toISOString().slice(0, 10);
      const held = (all || []).filter(
        (b) => b.status === 'pending_hold' && (b.date || '') >= todayIso,
      );
      setHeldBookings(held);
      const fromUrl = searchParams.get('booking_id');
      const found = list.find((b) => b.booking_id === fromUrl);
      if (found) setSelectedBookingId(found.booking_id);
      else if (list.length > 0) setSelectedBookingId(list[0].booking_id);
    });
  }, [searchParams]);

  // Whenever the user picks a different booking, sync child_name / age /
  // party_date / party_time so the invitation always matches the linked
  // party.
  useEffect(() => {
    if (!selectedBookingId || !upcomingBookings) return;
    const b = upcomingBookings.find((x) => x.booking_id === selectedBookingId);
    if (!b) return;
    setForm((f) => ({
      ...f,
      child_name: b.child_name || f.child_name,
      age: b.child_age || f.age,
      party_date: b.date || f.party_date,
      party_time: b.time_slot === '10:00' ? '10:00 AM' : b.time_slot === '13:00' ? '1:00 PM' : '4:00 PM',
    }));
  }, [selectedBookingId, upcomingBookings]);

  // ---- Photo upload helpers ----
  const uploadPhotoFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error('Please choose an image file (JPG, PNG, WebP, HEIC)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5 MB. Try a smaller image.');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API_URL}/api/customer/invitations/upload-photo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      const fullUrl = url && url.startsWith('/api/') ? `${API_URL}${url}` : url;
      setForm((f) => ({ ...f, photo_url: fullUrl }));
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadPhotoFile(f);
  };

  // Inline image uploader for the RichTextEditor's Image button. Reuses the
  // same backend endpoint as the hero photo so file-size / type rules match.
  const uploadInlineImage = async (file) => {
    if (!/^image\//.test(file.type)) {
      toast.error('Please choose an image file (JPG, PNG, WebP, HEIC)');
      throw new Error('not an image');
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5 MB. Try a smaller image.');
      throw new Error('too large');
    }
    const fd = new FormData();
    fd.append('image', file);
    const res = await axios.post(`${API_URL}/api/customer/invitations/upload-photo`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = res.data?.url;
    const fullUrl = url && url.startsWith('/api/') ? `${API_URL}${url}` : url;
    if (!fullUrl) throw new Error('upload returned no URL');
    toast.success('Image inserted');
    return fullUrl;
  };

  const template = useMemo(() => templates.find((t) => t.id === templateId) || templates[0] || {}, [templates, templateId]);

  const recipients = useMemo(() => {
    const manual = recipientRows.map((r) => ({ name: r.name.trim(), email: r.email.trim(), phone: r.phone.trim() }))
      .filter((r) => r.email || r.phone);
    return [...manual, ...parsePasted(pasted)];
  }, [recipientRows, pasted]);

  const updateRow = (idx, field, val) => {
    setRecipientRows((rows) => rows.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };
  const addRow = () => setRecipientRows((rows) => [...rows, { name: '', email: '', phone: '' }]);
  const removeRow = (idx) => setRecipientRows((rows) => rows.filter((_, i) => i !== idx));

  const handleCSVFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPasted((prev) => (prev ? prev + '\n' : '') + (reader.result || ''));
      toast.success(`Loaded ${file.name}`);
    };
    reader.readAsText(file);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.child_name.trim()) return 'Please enter the birthday child\'s name';
      if (!form.party_date) return 'Pick a party date';
      if (!form.party_time) return 'Pick a party time';
    }
    if (step === 2 && recipients.length === 0) return 'Add at least one recipient';
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const createAndSend = async () => {
    if (!selectedBookingId) {
      toast.error('Please select an upcoming party first');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        booking_id: selectedBookingId,                  // ← Iter-90
        template_id: templateId,
        child_name: form.child_name.trim(),
        age: parseInt(form.age, 10) || 0,
        party_date: form.party_date,
        party_time: form.party_time,
        location: form.location,
        message: form.message,
        photo_url: form.photo_url || null,
        send_email: form.send_email,
        send_sms: form.send_sms,
      };
      const cr = await axios.post(`${API_URL}/api/customer/invitations`, payload, { withCredentials: true });
      const id = cr.data.invitation_id;
      if (recipients.length > 0) {
        await axios.post(`${API_URL}/api/customer/invitations/${id}/recipients`, { recipients }, { withCredentials: true });
      }
      const sendRes = await axios.post(`${API_URL}/api/customer/invitations/${id}/send`, {}, { withCredentials: true });
      toast.success(`Sent to ${sendRes.data.sent} of ${sendRes.data.total} recipients`);
      navigate(`/my-invites/${id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Something went wrong creating your invitation';
      toast.error(msg);
      if (e.response?.status === 401) navigate('/login?next=/my-invites/new');
    } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="invite-create-page">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-3xl">
        <button onClick={() => navigate('/my-invites')} className="text-sm text-[#6B6B7A] hover:text-[#1E1E24] mb-2" data-testid="back-to-invites">
          ← Back to invites
        </button>
        <h1 className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Create Birthday Invite</h1>

        {/* Iter-90: A linked booking is mandatory. Show empty-state OR the
            booking-picker card before any form fields appear. */}
        {upcomingBookings === null ? (
          <p className="text-sm text-[#6B6B7A] mt-6">Loading your bookings…</p>
        ) : upcomingBookings.length === 0 && heldBookings.length > 0 ? (
          // iter-135 — Customer has a Pay-Later hold but no confirmed booking.
          // Show the specific "complete payment to confirm" message + a
          // Pay Now CTA that deep-links to the payment page.
          <div className="mt-6 rounded-2xl bg-[#FFF4E6] border-2 border-[#FF7A2E]/40 p-6 text-center" data-testid="invite-blocked-by-hold">
            <p className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
              Payment required before sending invitations
            </p>
            <p className="text-sm text-[#6B6B7A] mt-2 max-w-md mx-auto">
              Please complete your payment to confirm your booking before sending invitations. Your slot is currently on a Pay-Later hold.
            </p>
            <div className="mt-4 inline-flex flex-col items-center gap-2">
              {heldBookings.slice(0, 3).map((b) => (
                <div key={b.booking_id} className="flex items-center gap-2">
                  <span className="text-xs text-[#6B6B7A]">{b.date} · {b.child_name}'s party</span>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/booking/${b.booking_id}/payment`)}
                    className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs px-3"
                    data-testid={`invite-pay-now-${b.booking_id}`}
                  >
                    Pay now
                  </Button>
                </div>
              ))}
            </div>
            <Button
              onClick={() => navigate('/book')}
              variant="outline"
              className="rounded-full mt-4"
              data-testid="invite-book-another"
            >
              Or book another party →
            </Button>
          </div>
        ) : upcomingBookings.length === 0 ? (
          <div className="mt-6 rounded-2xl bg-[#FFE9EE] border-2 border-[#FF3B6B]/20 p-6 text-center" data-testid="invite-no-bookings">
            <p className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
              You have no upcoming parties.
            </p>
            <p className="text-sm text-[#6B6B7A] mt-1">Please book a party before sending invitations.</p>
            <Button
              onClick={() => navigate('/book')}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white mt-4"
              data-testid="invite-book-cta"
            >
              Book a party →
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-4 rounded-2xl bg-white border border-[#E8E5DF] p-4 sm:p-5" data-testid="invite-booking-picker">
              <p className="text-[11px] uppercase tracking-wide text-[#6B6B7A] font-semibold mb-2">
                Linked Party (required)
              </p>
              <select
                value={selectedBookingId}
                onChange={(e) => setSelectedBookingId(e.target.value)}
                className="w-full rounded-xl border border-[#E8E5DF] px-3 py-2 text-sm bg-white"
                data-testid="invite-booking-select"
              >
                {upcomingBookings.map((b) => (
                  <option key={b.booking_id} value={b.booking_id}>
                    {b.date} · {b.time_slot} · {b.child_name}'s {b.package_name || 'party'}
                    {b.booking_reference ? ` (${b.booking_reference})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-[#6B6B7A] mt-1.5">
                Invitation will be linked to this party so guests RSVPing can be tracked here.
              </p>
            </div>
            <div className="mt-3 flex items-center gap-2" data-testid="invite-stepper">
              {STEPS.map((s, i) => (
                <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= step ? 'bg-[#FF3B6B]' : 'bg-[#E8E5DF]'}`} />
              ))}
            </div>
            <p className="text-xs text-[#6B6B7A] mt-1">Step {step + 1} of {STEPS.length}: <strong>{STEPS[step]}</strong></p>

            <div className="mt-6">
          {/* STEP 0: TEMPLATE */}
          {step === 0 && (
            <div data-testid="step-template">
              <div className="grid sm:grid-cols-3 gap-4">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTemplateId(t.id)}
                    className={`text-left rounded-2xl p-5 border-2 transition-all ${templateId === t.id ? 'border-[#FF3B6B] shadow-lg' : 'border-[#E8E5DF] hover:border-[#FF3B6B]/40'}`}
                    style={{ background: t.bg_gradient }}
                    data-testid={`template-${t.id}`}
                  >
                    <div className="text-4xl mb-2">{t.icon}</div>
                    <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{t.name}</p>
                    <p className="text-xs text-[#6B6B7A] mt-1">{t.tagline}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1: DETAILS */}
          {step === 1 && (
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 space-y-4" data-testid="step-details">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#6B6B7A]">Child's name *</label>
                  <Input value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} className="mt-1" data-testid="invite-child-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B6B7A]">Age</label>
                  <Input type="number" min="1" max="25" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1" data-testid="invite-age" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B6B7A]">Party date *</label>
                  <Input type="date" value={form.party_date} onChange={(e) => setForm({ ...form, party_date: e.target.value })} className="mt-1" data-testid="invite-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B6B7A]">Party time *</label>
                  <Input value={form.party_time} onChange={(e) => setForm({ ...form, party_time: e.target.value })} placeholder="e.g. 10:00 AM" className="mt-1" data-testid="invite-time" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B6B7A]">Location</label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" data-testid="invite-location" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B6B7A]">Personal message</label>
                <p className="text-[11px] text-[#6B6B7A] mt-0.5 mb-1.5">
                  Use the toolbar to change font, size, color and add bold / italic / underline.
                </p>
                <RichTextEditor
                  value={form.message}
                  onChange={(html) => setForm({ ...form, message: html })}
                  placeholder="We'd love for you to celebrate with us!"
                  minHeight={140}
                  testId="invite-message-rte"
                  onImageUpload={uploadInlineImage}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B6B7A]">Photo (optional)</label>
                {form.photo_url ? (
                  <div className="mt-1 relative inline-block">
                    <img src={form.photo_url} alt="invitation" className="rounded-xl max-h-40 border-4 border-white shadow" data-testid="invite-photo-preview" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, photo_url: '' })}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-[#E8E5DF] shadow-sm flex items-center justify-center hover:bg-[#FFE4EB]"
                      data-testid="invite-photo-remove"
                      aria-label="Remove photo"
                    >
                      <X className="w-3.5 h-3.5 text-[#FF3B6B]" />
                    </button>
                  </div>
                ) : (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`mt-1 cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragOver ? 'border-[#FF3B6B] bg-[#FFE4EB]/40' : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50 bg-[#FFFDF8]'}`}
                    data-testid="invite-photo-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadPhotoFile(e.target.files?.[0])}
                      data-testid="invite-photo-input"
                    />
                    <ImagePlus className="w-7 h-7 text-[#FF3B6B] mx-auto mb-1" />
                    <p className="text-sm font-medium text-[#1E1E24]">
                      {uploadingPhoto ? 'Uploading…' : 'Drop a photo here, or click to choose'}
                    </p>
                    <p className="text-[11px] text-[#6B6B7A] mt-0.5">JPG, PNG, WebP, HEIC · up to 5 MB · auto-resized to 1200px</p>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="invite-send-email" />
                  Send via Email
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.send_sms} onChange={(e) => setForm({ ...form, send_sms: e.target.checked })} data-testid="invite-send-sms" />
                  Send via SMS (mocked)
                </label>
              </div>
            </div>
          )}

          {/* STEP 2: RECIPIENTS */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-recipients">
              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-[#1E1E24]"><Users className="w-4 h-4 inline mr-1.5" />Add recipients</p>
                  <Button type="button" onClick={addRow} className="rounded-full bg-[#1E1E24] text-white h-8 px-3 text-xs" data-testid="add-recipient-row">
                    <Plus className="w-3 h-3 mr-1" /> Row
                  </Button>
                </div>
                <div className="space-y-2">
                  {recipientRows.map((r, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`recipient-row-${idx}`}>
                      <Input value={r.name} onChange={(e) => updateRow(idx, 'name', e.target.value)} placeholder="Name" className="col-span-3 h-9" />
                      <Input value={r.email} onChange={(e) => updateRow(idx, 'email', e.target.value)} placeholder="email@example.com" className="col-span-5 h-9" type="email" />
                      <Input value={r.phone} onChange={(e) => updateRow(idx, 'phone', e.target.value)} placeholder="Phone (optional)" className="col-span-3 h-9" />
                      <button type="button" onClick={() => removeRow(idx)} className="col-span-1 p-1.5 rounded-full hover:bg-[#FFE4EB]">
                        <Trash2 className="w-4 h-4 text-[#FF3B6B]" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
                <p className="font-semibold text-[#1E1E24] mb-2"><FileSpreadsheet className="w-4 h-4 inline mr-1.5" />Or paste/upload CSV</p>
                <p className="text-xs text-[#6B6B7A] mb-2">One per line: <code>Name, email@example.com, +14165551234</code> — phone is optional.</p>
                <Textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={5} placeholder="Alice, alice@email.com, +14165551234" data-testid="csv-paste" />
                <div className="mt-2">
                  <label className="inline-flex items-center text-sm cursor-pointer text-[#FF3B6B] hover:underline">
                    <input type="file" accept=".csv,.txt" onChange={handleCSVFile} className="hidden" data-testid="csv-upload" />
                    Upload .csv file
                  </label>
                </div>
              </div>

              <p className="text-sm text-[#6B6B7A]" data-testid="recipient-count">
                Total recipients: <strong className="text-[#1E1E24]">{recipients.length}</strong>
              </p>
            </div>
          )}

          {/* STEP 3: PREVIEW & SEND */}
          {step === 3 && (
            <div data-testid="step-preview">
              <div className="rounded-2xl p-6 sm:p-8 text-center" style={{ background: template.bg_gradient }}>
                <div className="text-4xl mb-1">{template.icon}</div>
                <p className="text-xs text-[#6B6B7A] mb-1">{template.tagline}</p>
                <h2 className="text-2xl font-bold mt-1" style={{ color: template.accent, fontFamily: 'Fredoka' }}>You're invited!</h2>
                {form.photo_url && (
                  <img src={form.photo_url} alt="" className="mx-auto mt-4 rounded-xl max-h-48 object-cover border-4 border-white shadow" />
                )}
                <h3 className="text-xl font-semibold mt-4 text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
                  {form.child_name}'s {form.age}{['st','nd','rd'][((parseInt(form.age,10)||0)%10)-1] && (((parseInt(form.age,10)||0)%100<11||(parseInt(form.age,10)||0)%100>13))? ['st','nd','rd'][((parseInt(form.age,10)||0)%10)-1] : 'th'} Birthday
                </h3>
                <p className="text-sm text-[#1E1E24] mt-2">📅 {form.party_date} · ⏰ {form.party_time}</p>
                <p className="text-sm text-[#1E1E24]">📍 {form.location}</p>
                {form.message && (
                  <div
                    className="text-sm text-[#1E1E24] bg-white/70 rounded-lg p-3 mt-3 max-w-md mx-auto cb-invite-message"
                    data-testid="invite-message-preview"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.message || '') }}
                  />
                )}
                <div className="mt-5 inline-block px-5 py-2 rounded-full text-white" style={{ background: template.accent }}>RSVP now</div>
              </div>
              <p className="text-sm text-[#6B6B7A] mt-4 text-center">
                Will be sent to <strong>{recipients.length}</strong> {recipients.length === 1 ? 'person' : 'people'} via{' '}
                {form.send_email ? 'Email' : ''}{form.send_email && form.send_sms ? ' + ' : ''}{form.send_sms ? 'SMS' : ''}.
              </p>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="mt-8 flex items-center justify-between">
          <Button variant="outline" onClick={prev} disabled={step === 0} className="rounded-full h-10 px-5" data-testid="invite-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white h-10 px-5" data-testid="invite-next">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={createAndSend} disabled={creating || recipients.length === 0 || !selectedBookingId} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white h-10 px-5" data-testid="invite-send">
              <Send className="w-4 h-4 mr-1.5" /> {creating ? 'Sending…' : 'Create & Send'}
            </Button>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteCreate;
